import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Detectar iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Verificar si ya está instalada como PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    
    if (isStandalone) {
      return; // Ya está instalada, no mostrar el prompt
    }

    // Verificar si el usuario ya descartó el prompt
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Mostrar de nuevo después de 7 días
    if (dismissedTime && daysSinceDismissed < 7) {
      return;
    }

    // Para iOS, mostrar instrucciones personalizadas
    if (isIOSDevice) {
      setTimeout(() => setShowPrompt(true), 3000);
      return;
    }

    // Para Chrome/Android - capturar el evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Mostrar el prompt después de un tiempo si hay un deferredPrompt
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <>
      {/* Banner principal */}
      <div 
        className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up"
        data-testid="pwa-install-banner"
      >
        <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl shadow-2xl p-4 border border-pink-400/30">
          <button 
            onClick={handleDismiss}
            className="absolute top-2 right-2 text-white/70 hover:text-white transition-colors"
            data-testid="pwa-dismiss-btn"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="bg-white rounded-xl p-2 flex-shrink-0">
              <img 
                src="https://res.cloudinary.com/dkbsqvkdl/image/upload/v1737657892/booty-legs-logo_k6wmzg.png" 
                alt="Booty&Legs" 
                className="w-12 h-12 object-contain"
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-bold text-lg">¡Instala nuestra App!</h3>
              <p className="text-white/80 text-sm">
                Accede más rápido a tus entrenamientos
              </p>
            </div>
          </div>
          
          <button
            onClick={handleInstallClick}
            className="w-full mt-4 bg-white text-pink-600 font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:bg-pink-50 transition-colors"
            data-testid="pwa-install-btn"
          >
            {isIOS ? (
              <>
                <Smartphone size={20} />
                Ver cómo instalar
              </>
            ) : (
              <>
                <Download size={20} />
                Instalar App
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal de instrucciones iOS */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end md:items-center justify-center p-4">
          <div 
            className="bg-gray-900 rounded-t-3xl md:rounded-3xl w-full max-w-md p-6 animate-slide-up"
            data-testid="ios-instructions-modal"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white text-xl font-bold">Instalar en iPhone/iPad</h3>
              <button 
                onClick={handleDismiss}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="bg-pink-600 text-white w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <p className="text-white font-medium">Toca el botón Compartir</p>
                  <p className="text-gray-400 text-sm">El ícono de cuadrado con flecha hacia arriba en Safari</p>
                  <div className="mt-2 bg-gray-800 rounded-lg p-3 inline-flex items-center gap-2">
                    <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span className="text-gray-300 text-sm">Compartir</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-pink-600 text-white w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <p className="text-white font-medium">Desliza y toca "Agregar a Inicio"</p>
                  <p className="text-gray-400 text-sm">Busca la opción con el ícono de ➕</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-pink-600 text-white w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <p className="text-white font-medium">Toca "Agregar"</p>
                  <p className="text-gray-400 text-sm">¡Listo! La app aparecerá en tu pantalla de inicio</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={handleDismiss}
              className="w-full mt-6 bg-pink-600 text-white font-semibold py-3 px-4 rounded-xl hover:bg-pink-700 transition-colors"
            >
              ¡Entendido!
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(100px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
